import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './BookletViewer.css';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const EXTRACTED_BASE = `${API_BASE.replace(/\/+$|$/, '')}/extracted`;

const encodePath = (filePath) => filePath.split('/').map(encodeURIComponent).join('/');
const isPageFile = (name) => /\.(x?html?|htm)$/i.test(name);

const naturalCompare = (a, b) => {
  const ax = [];
  const bx = [];
  a.toLowerCase().replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
    ax.push([$1 || Infinity, $2 || '']);
  });
  b.toLowerCase().replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
    bx.push([$1 || Infinity, $2 || '']);
  });

  while (ax.length && bx.length) {
    const [an, at] = ax.shift();
    const [bn, bt] = bx.shift();
    if (at !== bt) return at < bt ? -1 : 1;
    if (an !== bn) return an - bn;
  }

  return ax.length - bx.length;
};

const isNavigationPage = (fileName) => {
  const normalized = fileName.toLowerCase();
  return /(^nav|toc|page[-_ ]?(list|numbers|navigation)|index|cover|contents?)\./i.test(normalized);
};

const BookletViewer = () => {
  const { bookId } = useParams();
  const [contents, setContents] = useState([]);
  const [error, setError] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const iframeRef = useRef(null);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/extracted/list?dir=${encodeURIComponent(bookId)}&recursive=true`);
        if (!response.ok) {
          throw new Error('Failed to load book contents');
        }
        const data = await response.json();
        setContents(data.contents || []);
        setError('');
      } catch (err) {
        setError(err.message || 'Could not load book');
      }
    };

    fetchBook();
  }, [bookId]);

  const pageFiles = useMemo(
    () => contents
      .filter((item) => !item.isDirectory && isPageFile(item.name) && !isNavigationPage(item.name))
      .sort((a, b) => naturalCompare(a.path, b.path)),
    [contents]
  );

  const getUrl = (filePath) => `${API_BASE}/extracted/${encodePath(filePath)}`;

  const goPrev = () => {
    setCurrentPageIndex((value) => Math.max(0, value - 1));
    setIsPageLoading(true);
  };
  
  const goNext = () => {
    setCurrentPageIndex((value) => Math.min(pageFiles.length - 1, value + 1));
    setIsPageLoading(true);
  };

  useEffect(() => {
    setCurrentPageIndex(0);
    setIsPageLoading(true);
  }, [bookId]);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;

      // Strip controls from any video tags inside the EPUB
      doc.querySelectorAll('video').forEach(v => {
        v.removeAttribute('controls');
        
        // Check if the video is not full-page and adjust its styling
        setTimeout(() => {
          const containerWidth = v.parentElement ? v.parentElement.clientWidth : v.clientWidth;
          const bodyWidth = doc.body.clientWidth;
          
          // If the container is noticeably smaller than the full body width
          if (containerWidth && bodyWidth && containerWidth < bodyWidth * 0.9) {
            v.classList.add('partial-page-video');
          }
        }, 50);
      });

      // Base styles: center whatever sits inside <body>, and force media
      // elements (video/img/svg) to actually fill the frame instead of
      // rendering at their native HTML width/height attributes.
      let epubWidth = null;
      let epubHeight = null;
      
      // Try to detect fixed layout dimensions from viewport meta tag
      const viewportMeta = doc.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        const content = viewportMeta.getAttribute('content');
        if (content) {
          const widthMatch = content.match(/width\s*=\s*([0-9.]+)/i);
          const heightMatch = content.match(/height\s*=\s*([0-9.]+)/i);
          if (widthMatch && heightMatch) {
            epubWidth = parseFloat(widthMatch[1]);
            epubHeight = parseFloat(heightMatch[1]);
          }
        }
      }

      // Fallback: check if body has explicit inline width/height
      if (!epubWidth) {
        const bodyWidth = parseFloat(doc.body.style.width);
        const bodyHeight = parseFloat(doc.body.style.height);
        if (bodyWidth && bodyHeight) {
          epubWidth = bodyWidth;
          epubHeight = bodyHeight;
        }
      }

      const style = doc.createElement('style');
      let cssText = `
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important; 
          background: transparent !important;
        }
        ::-webkit-scrollbar {
          display: none !important;
        }

        img, svg {
          max-width: 100% !important;
          height: auto !important;
        }

        /* Target the div wrappers that InDesign generates for videos */
        div[class*="_idGenMedia"], div[class*="_idGenObjectAttribute"] {
          overflow: hidden !important;
        }
        
        video {
          position: absolute !important;
          top: 0 !important;
          left: 1px !important;
          margin: 0 !important;
          padding: 0 !important;
          width: calc(100% - 2px) !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          object-fit: cover !important;
          transform: none !important;
          transform-origin: top left !important;
          box-sizing: border-box !important;
        }

        video.partial-page-video {
          left: 0 !important;
          width: 100% !important;
        }

        video::-webkit-media-controls {
          display: none !important;
        }

        iframe, object {
          max-width: 100% !important;
        }
      `;

      // If it's a fixed-layout EPUB, perfectly scale and center it!
      if (epubWidth && epubHeight) {
        cssText += `
          html {
            width: 100% !important;
            height: 100% !important;
            display: block !important;
          }
          body {
            /* Force the body to its exact intended native size */
            width: ${epubWidth}px !important;
            height: ${epubHeight}px !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            margin: 0 !important;
            transform-origin: top left !important;
          }
        `;

        const updateScale = () => {
          const w = iframe.clientWidth;
          const h = iframe.clientHeight;
          const scale = Math.min((w * 0.96) / epubWidth, (h * 0.96) / epubHeight);
          
          const scaledWidth = epubWidth * scale;
          const scaledHeight = epubHeight * scale;
          const left = (w - scaledWidth) / 2;
          const top = (h - scaledHeight) / 2;

          if (doc.body) {
            doc.body.style.setProperty('transform', `translate(${left}px, ${top}px) scale(${scale})`, 'important');
          }
        };
        
        // Initial scale
        setTimeout(updateScale, 0);
        
        // Update scale on resize
        iframe.contentWindow.addEventListener('resize', updateScale);
        window.addEventListener('resize', updateScale);
      }

      style.textContent = cssText;
      doc.head.appendChild(style);

    } catch (e) {
      console.warn('Cross-origin restriction prevented deep asset injection.', e);
    }
    
    // Reveal the perfectly styled iframe after giving the browser a split-second to apply the CSS layout
    setTimeout(() => {
      setIsPageLoading(false);
    }, 50);
  };

  const currentPage = pageFiles[currentPageIndex];

  return (
    <div className="booklet-shell">
      <main className="booklet-main-container">
        {error && <div className="status error">{error}</div>}

        {pageFiles.length === 0 ? (
          <div className="booklet-empty">
            <p>No pages found for this book.</p>
          </div>
        ) : (
          <div className="booklet-viewer-workspace">

            {/* Perfectly Bound Center Content Frame */}
            <div className="booklet-center-frame">
              {!hasStarted ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px' }}>
                  <img 
                    src="/logo.png" 
                    alt="Brand Logo" 
                    style={{ 
                      height: '120px', 
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))' 
                    }} 
                  />
                  <button 
                    type="button"
                    onClick={() => setHasStarted(true)}
                    className="booklet-view-btn"
                  >
                    View
                  </button>
                </div>
              ) : (
                <>
                  {isPageLoading && <div className="booklet-loader"></div>}
                  <iframe
                    ref={iframeRef}
                    title={`page-${currentPageIndex + 1}`}
                    src={getUrl(currentPage.path)}
                    className="booklet-iframe"
                    allow="autoplay"
                    onLoad={handleIframeLoad}
                    style={{
                      opacity: isPageLoading ? 0 : 1,
                      transition: isPageLoading ? 'none' : 'opacity 0.25s ease-in-out'
                    }}
                  />
                </>
              )}
            </div>

            {/* Locked Bottom Floating Control Menu */}
            {hasStarted && (
              <div className="booklet-bottom-controls">
                <button
                  type="button"
                  className="control-btn"
                  onClick={goPrev}
                  disabled={currentPageIndex === 0}
                >
                  ⟨
                </button>

                <div className="booklet-page-badge">
                  {currentPageIndex + 1} / {pageFiles.length}
                </div>

                <button
                  type="button"
                  className="control-btn"
                  onClick={goNext}
                  disabled={currentPageIndex === pageFiles.length - 1}
                >
                  ⟩
                </button>
              </div>
            )}

            <div style={{ fontFamily: 'Rubik', fontSize: '0.75rem', color: '#94a3b8', zIndex: 10, textAlign: 'right', pointerEvents: 'auto' }}>
              DESIGNED & MANAGED BY <a href="https://systechlogic.com/" target="_blank" rel="noopener noreferrer" className="footer-brand-link" style={{ color: '#60a5fa' }}>SYSTECHLOGIC</a>
            </div>

          </div>
        )}
      </main>
    </div>
  );
};

export default BookletViewer;
